// @ts-nocheck
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import { pxToRem } from "@toolkit/Framework/Common/Utilities/unitHelper";
import {
    BomLineModel,
    ConfigureResponseModel,
    ConfigureSuccessModel,
} from "@store/Framework/Types/ValveConfiguratorApiModels";
import Block from "@store/Library/Components/Toolkit/Block";
import getColor from "@insite/mobius/utilities/getColor";
import { useSettingsCollection } from "@toolkit/Framework/Store/Context/ContextHooks";
import React, { FC, useEffect, useMemo, useRef, useState } from "react";
import { connect } from "react-redux";
import styled from "styled-components";

// Brand-aligned palette: every entry resolves to a token from the store theme
// (src/Themes/common/styleguide/config/colors.ts). No new colors are introduced.
const FLS = {
    cta: getColor("primary"),
    ctaHover: getColor("primary.dark"),
    ink: getColor("text.main"),
    text: getColor("text.main"),
    muted: getColor("text.accent"),
    subtle: getColor("placeholder"),
    border: getColor("common.border"),
    rowAlt: getColor("common.borderLight"),
    chipBg: getColor("common.accent"),
    background: getColor("common.background"),
};

const mapStateToProps = (state: ApplicationState) => {
    const v = state.store.data.valveConfigurator;
    return {
        configurationResult: v.configurationResult,
        hasSubmitted: v.hasSubmitted,
    };
};

type Props = WidgetProps & ReturnType<typeof mapStateToProps>;

const Section = styled.section`
    border: 1px solid ${FLS.border};
    border-radius: ${pxToRem(6)};
    background: ${FLS.background};
    box-shadow: 0 1px 3px rgba(0, 0, 0, 0.04);
    overflow: hidden;
`;

const Header = styled.button`
    display: flex;
    width: 100%;
    align-items: center;
    justify-content: space-between;
    padding: ${pxToRem(12)} ${pxToRem(20)};
    background: transparent;
    border: 0;
    cursor: pointer;
    font-size: ${pxToRem(15)};
    font-weight: 600;
    color: ${FLS.ink};
    text-align: left;

    .chev {
        transition: transform 200ms ease;
        font-size: ${pxToRem(11)};
        color: ${FLS.muted};
    }

    &.open .chev {
        transform: rotate(180deg);
    }
`;

const Body = styled.div`
    border-top: 1px solid ${FLS.border};
`;

const CarouselWrap = styled.div`
    position: relative;
`;

const Carousel = styled.div`
    display: flex;
    gap: ${pxToRem(16)};
    overflow-x: auto;
    padding: ${pxToRem(20)} ${pxToRem(20)} ${pxToRem(8)};
    scroll-snap-type: x mandatory;
    scroll-padding-left: ${pxToRem(20)};
    scroll-padding-right: ${pxToRem(20)};
    scroll-behavior: smooth;

    & > * {
        scroll-snap-align: start;
    }

    /* Hide scrollbar; nav controls handle paging. */
    scrollbar-width: none;
    &::-webkit-scrollbar {
        display: none;
    }
`;

const Card = styled.article`
    width: ${pxToRem(280)};
    flex-shrink: 0;
    border: 1px solid ${FLS.border};
    border-radius: ${pxToRem(8)};
    background: ${FLS.background};
    padding: ${pxToRem(14)};
    display: flex;
    flex-direction: column;
    gap: ${pxToRem(12)};
`;

const CardTop = styled.div`
    display: flex;
    gap: ${pxToRem(14)};
    align-items: flex-start;
`;

const Thumb = styled.div`
    flex: 0 0 auto;
    width: ${pxToRem(84)};
    height: ${pxToRem(84)};
    border-radius: ${pxToRem(6)};
    background: ${FLS.chipBg};
    border: 1px solid ${FLS.border};
    display: flex;
    align-items: center;
    justify-content: center;
    color: ${FLS.subtle};
    font-size: ${pxToRem(10)};
    text-transform: uppercase;
    letter-spacing: 0.08em;
    overflow: hidden;

    img {
        width: 100%;
        height: 100%;
        object-fit: contain;
    }
`;

const CardMeta = styled.div`
    flex: 1 1 auto;
    min-width: 0;
    display: flex;
    flex-direction: column;
    gap: ${pxToRem(4)};
`;

const Brand = styled.span`
    font-size: ${pxToRem(11)};
    font-weight: 600;
    color: ${FLS.cta};
    display: inline-flex;
    align-items: center;
    gap: ${pxToRem(6)};

    &::before {
        content: "";
        width: ${pxToRem(6)};
        height: ${pxToRem(6)};
        border-radius: 50%;
        background: ${FLS.cta};
        display: inline-block;
    }
`;

const Description = styled.span`
    font-size: ${pxToRem(14)};
    font-weight: 700;
    color: ${FLS.ink};
    line-height: 1.3;
    overflow: hidden;
    text-overflow: ellipsis;
    display: -webkit-box;
    -webkit-line-clamp: 2;
    -webkit-box-orient: vertical;
`;

const PartNumber = styled.span`
    font-size: ${pxToRem(11)};
    color: ${FLS.muted};
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
`;

const NavBar = styled.div`
    display: flex;
    align-items: center;
    justify-content: center;
    gap: ${pxToRem(16)};
    padding: ${pxToRem(8)} ${pxToRem(20)} ${pxToRem(16)};
`;

const NavButton = styled.button`
    width: ${pxToRem(28)};
    height: ${pxToRem(28)};
    border-radius: 50%;
    border: 0;
    background: transparent;
    color: ${FLS.ink};
    font-size: ${pxToRem(16)};
    cursor: pointer;
    display: inline-flex;
    align-items: center;
    justify-content: center;

    &:hover:not(:disabled) {
        background: ${FLS.chipBg};
    }

    &:disabled {
        color: ${FLS.subtle};
        cursor: default;
    }
`;

const ProgressTrack = styled.div`
    flex: 0 1 ${pxToRem(180)};
    height: ${pxToRem(4)};
    background: ${FLS.border};
    border-radius: ${pxToRem(2)};
    position: relative;
    overflow: hidden;
`;

const ProgressThumb = styled.div<{ $left: number; $width: number }>`
    position: absolute;
    top: 0;
    bottom: 0;
    left: ${({ $left }) => `${$left}%`};
    width: ${({ $width }) => `${$width}%`};
    background: ${FLS.ink};
    border-radius: ${pxToRem(2)};
    transition: left 200ms ease, width 200ms ease;
`;

const ValveConfiguratorSparePartsWidget: FC<Props> = ({ configurationResult, hasSubmitted }) => {
    const [open, setOpen] = useState(true);
    const carouselRef = useRef<HTMLDivElement | null>(null);
    const [scrollState, setScrollState] = useState({ left: 0, max: 0, view: 0 });

    const lines = useMemo<BomLineModel[]>(() => {
        if (!hasSubmitted || !configurationResult) {
            return [];
        }
        const r = configurationResult as ConfigureResponseModel;
        if (r.isQto) {
            return [];
        }
        const success = r as ConfigureSuccessModel;
        return [...(success.sparePartsList?.recommended ?? []), ...(success.sparePartsList?.optional ?? [])];
    }, [configurationResult, hasSubmitted]);

    // Spare parts are reference-only here: no price or availability is shown,
    // so we deliberately skip the bomPrices / realtimeinventory round-trips.
    const { extensionCatalogSettings } = useSettingsCollection();
    const notFoundImage = extensionCatalogSettings?.notFoundSmallImagePath;

    const handleScroll = () => {
        const el = carouselRef.current;
        if (!el) {
            return;
        }
        setScrollState({
            left: el.scrollLeft,
            max: el.scrollWidth - el.clientWidth,
            view: el.clientWidth,
        });
    };

    // Measure on mount, when the carousel opens, when its contents change, and
    // on window resize — otherwise the nav bar (which only renders when
    // scrollState.max > 0) never appears until the user manually scrolls.
    useEffect(() => {
        if (!open) {
            return;
        }
        // Defer one frame so the carousel has laid out after toggling `open`.
        const id = window.requestAnimationFrame(handleScroll);
        window.addEventListener("resize", handleScroll);
        return () => {
            window.cancelAnimationFrame(id);
            window.removeEventListener("resize", handleScroll);
        };
    }, [open, lines.length]);

    const scrollByCard = (dir: 1 | -1) => {
        const el = carouselRef.current;
        if (!el) {
            return;
        }
        const card = el.querySelector("article") as HTMLElement | null;
        const step = card ? card.offsetWidth + 16 : el.clientWidth * 0.8;
        el.scrollBy({ left: dir * step, behavior: "smooth" });
    };

    if (!hasSubmitted || !configurationResult || configurationResult.isQto) {
        return null;
    }
    if (lines.length === 0) {
        return null;
    }

    const max = scrollState.max || 1;
    const trackPct = scrollState.view && scrollState.max
        ? Math.max(15, Math.min(100, (scrollState.view / (scrollState.view + scrollState.max)) * 100))
        : 30;
    const leftPct = (scrollState.left / max) * (100 - trackPct);
    const atStart = scrollState.left <= 1;
    const atEnd = scrollState.left >= max - 1;

    return (
        <Block>
            <Section>
                <Header
                    type="button"
                    className={open ? "open" : ""}
                    onClick={() => setOpen(o => !o)}
                    data-test-selector="valveSparesToggle"
                    aria-expanded={open}
                >
                    <span>{translate("FlsValveConfigurator.SpareParts.Title")}</span>
                    <span className="chev" aria-hidden="true">
                        ▼
                    </span>
                </Header>

                {open ? (
                    <Body>
                        <CarouselWrap>
                            <Carousel
                                ref={carouselRef}
                                onScroll={handleScroll}
                                role="region"
                                aria-label={translate("FlsValveConfigurator.SpareParts.Title")}
                            >
                                {lines.map((line, i) => {
                                    const thumbSrc = line.previewImageUrl || notFoundImage || "";
                                    return (
                                        <Card key={`${line.partNumber}-${i}`}>
                                            <CardTop>
                                                <Thumb aria-hidden="true">
                                                    {thumbSrc ? (
                                                        <img src={thumbSrc} alt="" />
                                                    ) : (
                                                        translate("FlsValveConfigurator.SpareParts.NoImage")
                                                    )}
                                                </Thumb>
                                                <CardMeta>
                                                    <Description>{line.description}</Description>
                                                    <PartNumber>
                                                        {translate("{0}:", translate("Part #"))}{" "}
                                                        {line.partNumber || "—"}
                                                    </PartNumber>
                                                </CardMeta>
                                            </CardTop>
                                        </Card>
                                    );
                                })}
                            </Carousel>

                            {scrollState.max > 0 ? (
                                <NavBar>
                                    <NavButton
                                        type="button"
                                        onClick={() => scrollByCard(-1)}
                                        disabled={atStart}
                                        aria-label="Previous"
                                    >
                                        ←
                                    </NavButton>
                                    <ProgressTrack>
                                        <ProgressThumb $left={leftPct} $width={trackPct} />
                                    </ProgressTrack>
                                    <NavButton
                                        type="button"
                                        onClick={() => scrollByCard(1)}
                                        disabled={atEnd}
                                        aria-label="Next"
                                    >
                                        →
                                    </NavButton>
                                </NavBar>
                            ) : null}
                        </CarouselWrap>
                    </Body>
                ) : null}
            </Section>
        </Block>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps)(ValveConfiguratorSparePartsWidget),
    definition: {
        group: "Products",
        icon: "shopping-cart",
        displayName: "Valve Configurator - Spare Parts",
        fieldDefinitions: [],
    },
};

export default widgetModule;
